"use client";

import {
  Flame,
  Headphones,
  Heart,
  type LucideIcon,
  Music2,
  Sparkles,
  Star,
  Zap,
} from "lucide-react";
import type { CSSProperties } from "react";

/**
 * Die-cut stickers slapped on a CD case. Same material language as the wallet
 * stickers (`components/wallet/sticker-blob.tsx`): a white die-cut margin, a
 * holographic rim and a glossy highlight — painted once, nothing animates.
 */

// Thin-film rainbow, as on the wallet stickers' rim.
const HOLO =
  "conic-gradient(from 210deg, #ffb4e6, #d4b8ff, #9cc8ff, #9ff3e4, #fff0a8, #ffc59a, #ffb4e6)";
const GLOSS =
  "linear-gradient(160deg, rgba(255,255,255,0.75) 0%, rgba(255,255,255,0.15) 38%, rgba(255,255,255,0) 55%)";
const SCALLOP =
  "polygon(50% 0%, 61% 8%, 76% 5%, 83% 19%, 97% 25%, 92% 40%, 100% 50%, 92% 61%, 97% 75%, 83% 82%, 76% 96%, 61% 92%, 50% 100%, 39% 92%, 24% 96%, 17% 82%, 3% 75%, 8% 60%, 0% 50%, 8% 40%, 3% 25%, 17% 19%, 24% 5%, 39% 8%)";

const DIE_CUT_SHADOW =
  "drop-shadow(0 1px 0 rgba(0,0,0,0.08)) drop-shadow(0 2px 3px rgba(15,23,42,0.28))";

export type MusicStickerSpec =
  | {
      kind: "pill";
      text: string;
      fill: string;
      ink: string;
      holo?: boolean;
    }
  | { kind: "burst"; icon: StickerIconName; fill: string; ink: string }
  | { kind: "badge"; text: string; sub?: string; fill: string; ink: string }
  | { kind: "explicit" };

export type StickerIconName =
  | "heart"
  | "star"
  | "flame"
  | "zap"
  | "sparkles"
  | "headphones"
  | "music";

const ICONS: Record<StickerIconName, LucideIcon> = {
  heart: Heart,
  star: Star,
  flame: Flame,
  zap: Zap,
  sparkles: Sparkles,
  headphones: Headphones,
  music: Music2,
};

export interface PlacedSticker {
  spec: MusicStickerSpec;
  /** Percent of the case, from the top-left. */
  x: number;
  y: number;
  rotate: number;
  /** Fraction of the case size. */
  scale: number;
}

/** `size` is the CASE size in px; stickers scale with it. */
export function MusicSticker({
  sticker,
  size,
}: {
  sticker: PlacedSticker;
  size: number;
}) {
  const unit = (size / 100) * sticker.scale;
  const style: CSSProperties = {
    left: `${sticker.x}%`,
    top: `${sticker.y}%`,
    transform: `translate(-50%, -50%) rotate(${sticker.rotate}deg)`,
    filter: DIE_CUT_SHADOW,
  };

  return (
    <span aria-hidden className="pointer-events-none absolute" style={style}>
      <StickerBody spec={sticker.spec} unit={unit} />
    </span>
  );
}

function StickerBody({ spec, unit }: { spec: MusicStickerSpec; unit: number }) {
  switch (spec.kind) {
    case "pill":
      return (
        <span
          className="relative block overflow-hidden rounded-full bg-white"
          style={{ padding: 1.6 * unit }}
        >
          {spec.holo ? (
            <span
              className="absolute inset-0 opacity-90"
              style={{ background: HOLO }}
            />
          ) : null}
          <span
            className="relative block rounded-full leading-none font-black whitespace-nowrap uppercase"
            style={{
              background: spec.fill,
              color: spec.ink,
              fontSize: 6.2 * unit,
              letterSpacing: "0.04em",
              padding: `${2.4 * unit}px ${4.6 * unit}px`,
              maxWidth: 46 * unit * 2,
            }}
          >
            <span className="block truncate">{spec.text}</span>
          </span>
          <span
            className="pointer-events-none absolute inset-0 rounded-full"
            style={{ background: GLOSS }}
          />
        </span>
      );
    case "burst": {
      const Icon = ICONS[spec.icon];
      const box = 24 * unit;
      return (
        <span
          className="relative block"
          style={{ width: box, height: box, clipPath: SCALLOP }}
        >
          <span
            className="absolute inset-[-20%]"
            style={{ background: HOLO }}
          />
          <span
            className="absolute inset-[7%] flex items-center justify-center"
            style={{
              clipPath: SCALLOP,
              background: `radial-gradient(120% 120% at 28% 20%, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0) 45%), ${spec.fill}`,
            }}
          >
            <Icon
              strokeWidth={2.6}
              style={{
                width: box * 0.46,
                height: box * 0.46,
                color: spec.ink,
                fill:
                  spec.icon === "heart" || spec.icon === "star"
                    ? spec.ink
                    : "none",
              }}
            />
          </span>
          <span
            className="absolute top-[14%] left-[20%] h-[18%] w-[30%] rounded-full"
            style={{
              background:
                "radial-gradient(closest-side, rgba(255,255,255,0.85), rgba(255,255,255,0))",
              transform: "rotate(-28deg)",
            }}
          />
        </span>
      );
    }
    case "badge": {
      const box = 25 * unit;
      return (
        <span
          className="relative flex flex-col items-center justify-center rounded-full bg-white"
          style={{ width: box, height: box, padding: 1.6 * unit }}
        >
          <span
            className="flex h-full w-full flex-col items-center justify-center rounded-full text-center leading-[0.9] font-black uppercase"
            style={{
              background: spec.fill,
              color: spec.ink,
              boxShadow: `inset 0 0 0 ${0.9 * unit}px rgba(255,255,255,0.55)`,
            }}
          >
            <span style={{ fontSize: 8.4 * unit }}>{spec.text}</span>
            {spec.sub ? (
              <span
                className="font-bold"
                style={{ fontSize: 3.4 * unit, letterSpacing: "0.08em" }}
              >
                {spec.sub}
              </span>
            ) : null}
          </span>
          <span
            className="pointer-events-none absolute inset-0 rounded-full"
            style={{ background: GLOSS }}
          />
        </span>
      );
    }
    case "explicit":
      return (
        <span
          className="block rounded-[2px] bg-white"
          style={{ padding: 1.2 * unit }}
        >
          <span
            className="flex items-center justify-center rounded-[1px] bg-neutral-900 leading-none font-black text-white"
            style={{
              width: 8.5 * unit,
              height: 8.5 * unit,
              fontSize: 6 * unit,
            }}
          >
            E
          </span>
        </span>
      );
  }
}

// ── Deterministic layout ─────────────────────────────────────────────────────

/** FNV-1a; the same song always wears the same stickers. */
function hash(seed: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

function pick<T>(items: readonly T[], seed: number): T {
  return items[seed % items.length] as T;
}

const PALETTES = [
  { fill: "#ff5fa2", ink: "#ffffff" },
  { fill: "#ffd23f", ink: "#1b1b1f" },
  { fill: "#7c5cff", ink: "#ffffff" },
  { fill: "#2ee6a6", ink: "#0d2b22" },
  { fill: "#ff7a3d", ink: "#ffffff" },
  { fill: "#5ac8ff", ink: "#0b2233" },
] as const;

const ICON_ROTATION: StickerIconName[] = [
  "heart",
  "star",
  "flame",
  "zap",
  "sparkles",
  "headphones",
];

/**
 * The stickers on one song's case: a genre pill, a holo burst, a "#1 / on
 * repeat" badge for the top pick, and the parental-advisory "E" when it
 * applies. Placed around the edges so the cover art stays readable.
 */
export function stickersForSong(song: {
  id: string;
  genreNames: string[];
  explicit: boolean;
  position: number;
}): PlacedSticker[] {
  const h = hash(song.id);
  const palette = pick(PALETTES, h);
  const accent = pick(PALETTES, (h >>> 5) + 1);
  const stickers: PlacedSticker[] = [];

  const genre = song.genreNames[0];
  if (genre) {
    stickers.push({
      spec: {
        kind: "pill",
        text: genre,
        fill: palette.fill,
        ink: palette.ink,
        holo: (h & 1) === 1,
      },
      x: 34 + (h % 9),
      y: 84 - ((h >>> 3) % 5),
      rotate: -9 + ((h >>> 7) % 7),
      scale: 1,
    });
  }

  stickers.push({
    spec: {
      kind: "burst",
      icon: pick(ICON_ROTATION, h >>> 11),
      fill: accent.fill,
      ink: accent.ink,
    },
    x: 82 - ((h >>> 13) % 6),
    y: 17 + ((h >>> 17) % 8),
    rotate: 8 + ((h >>> 19) % 18),
    scale: 1,
  });

  if (song.position === 0) {
    stickers.push({
      spec: {
        kind: "badge",
        text: "#1",
        sub: "on repeat",
        fill: "#111114",
        ink: "#ffe14d",
      },
      x: 24,
      y: 20 + ((h >>> 21) % 6),
      rotate: -14 + ((h >>> 23) % 10),
      scale: 1,
    });
  }

  if (song.explicit) {
    stickers.push({
      spec: { kind: "explicit" },
      x: 88,
      y: 88,
      rotate: 0,
      scale: 1,
    });
  }

  return stickers;
}
